import { useEffect, useState } from 'react'
import type { ProposalSummary } from '../../services/aiService'
import { useNls } from '../../nls/nlsProvider'
import { AgentThinkingIndicator } from '../Line/AgentThinkingIndicator'
import { Button } from '../Common/Button'

const DISCARD_CONFIRMATION_MS = 3_000

type RevisionState = 'idle' | 'awaiting-user' | 'awaiting-line'

export interface ProposalSummaryCardProps {
  turnId: string
  summary?: ProposalSummary | null
  actionError?: string | null
  onRetry?: () => void
  onBuild: () => void
  onChange: () => void
  onDiscard: () => void
  revisionState: RevisionState
  collapsedEarlierProposal?: {
    taskCount: number
    subtaskCount: number
    checklistCount: number
  }
}

/**
 * The one pre-build surface for a READY_GENERATE proposal in both desktop and
 * member shells. Its callbacks deliberately remain owned by AiGeneratorPanel,
 * where preview persistence and permissions are resolved.
 */
export const ProposalSummaryCard = ({
  turnId,
  summary,
  actionError,
  onRetry,
  onBuild,
  onChange,
  onDiscard,
  revisionState,
  collapsedEarlierProposal,
}: ProposalSummaryCardProps) => {
  const { t } = useNls()
  const [discardConfirmationOpen, setDiscardConfirmationOpen] = useState(false)
  const awaitingUser = revisionState === 'awaiting-user'
  const awaitingLine = revisionState === 'awaiting-line'

  useEffect(() => {
    setDiscardConfirmationOpen(false)
  }, [turnId])

  useEffect(() => {
    if (!discardConfirmationOpen) return
    const timer = window.setTimeout(() => setDiscardConfirmationOpen(false), DISCARD_CONFIRMATION_MS)
    return () => window.clearTimeout(timer)
  }, [discardConfirmationOpen])

  const requestDiscard = () => {
    if (!discardConfirmationOpen) {
      setDiscardConfirmationOpen(true)
      return
    }
    setDiscardConfirmationOpen(false)
    onDiscard()
  }

  if (collapsedEarlierProposal) {
    return (
      <p className="aiProposalEarlierSummary">
        {t('NLSAiProposalEarlierSummary', {
          tasks: collapsedEarlierProposal.taskCount,
          subtasks: collapsedEarlierProposal.subtaskCount,
          checklists: collapsedEarlierProposal.checklistCount,
        })}
      </p>
    )
  }

  if (!summary) return null

  return (
    <>
      <section className="aiProposalSummary" role="group" aria-label={t('NLSAiProposalSummaryAria')} data-turn-id={turnId}>
        <div className="aiProposalSummaryHeader">
          <span className="aiProposalSummaryLabel">{t('NLSAiProposalSummaryHeard')}</span>
          <strong className="aiProposalSummaryHeading">{summary.heading}</strong>
        </div>
        {summary.bullets.length > 0 && (
          <ul className="aiProposalSummaryList">
            {summary.bullets.map((bullet, bulletIndex) => (
              <li key={bulletIndex}>{bullet}</li>
            ))}
          </ul>
        )}
        {awaitingUser && (
          <p className="aiProposalSummaryHint" role="status">{t('NLSAiProposalRevisionHint')}</p>
        )}
        <p className="aiProposalSummaryPrompt">{summary.buildPrompt}</p>
        {actionError ? (
          <span className="lineQuickActionsError" role="alert">{actionError}</span>
        ) : null}
        <div className="aiProposalSummaryActions">
          {awaitingLine ? (
            <div className="aiProposalSummaryThinking">
              <AgentThinkingIndicator label={t('NLSAiProposalChangingHint')} />
            </div>
          ) : (
            <Button
              size="sm"
              variant="primary"
              disabled={awaitingUser}
              onClick={actionError && onRetry ? onRetry : onBuild}
            >
              {actionError && onRetry ? t('NLSAiActionRetry') : t('NLSAiActionBuildIt')}
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={awaitingLine}
            onClick={onChange}
          >
            {t('NLSAiActionChangeSomething')}
          </Button>
          <Button
            size="sm"
            variant="tertiary"
            tone="danger"
            disabled={awaitingLine}
            onClick={requestDiscard}
          >
            {discardConfirmationOpen
              ? t('NLSAiProposalActionDiscardConfirm')
              : t('NLSAiProposalActionDiscard')}
          </Button>
        </div>
      </section>
    </>
  )
}
